import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'

export type AccountSettingsConfigType = {
  title: string
  button: JSX.Element
}

export type DateLimitValue = {
  title: DateLimitOptions
  dateLabel: string
  dateValue: Timestamp | null
  isRecommended: boolean
}

export type DateLimitRecommendations = {
  initialValue: DateLimitOptions
  recommendations: DateLimitConfigType
  // The account's stored month count, or the recommended default when it has none yet.
  slidingMonths: number
}

export type DateLimitConfigType = {
  [key in DateLimitOptions]: DateLimitValue | null
}

export enum AccountSections {
  EARLIEST_EVIDENCE_DATE = 'Earliest evidence date',
  IMAGE_TIMESTAMP_VALIDATION = 'Set image timestamp validation',
  AUDIT_SCAN_FREQUENCY = 'Control Test Frequency',
  GOOGLE_DRIVE = 'Google Drive',
  ANECDOTES = 'Anecdotes',
  SHAREPOINT = 'SharePoint',
  CONFLUENCE = 'Confluence',
  CONFIGURE_RISK_CATEGORY = 'Manage risk categories',
  CONFIGURE_DEPARTMENTS = 'Manage departments',
  CONFIGURE_CONTROL_STATUSES = 'Manage control statuses',
  CONFIGURE_RISK_STATUSES = 'Manage risk statuses',
  CONFIGURE_VENDOR_STATUSES = 'Manage vendor statuses',
  CHILD_REPORTING = 'Parent account reporting',
  CONFIGURE_LABELS = 'Manage labels',
  CONFIGURE_PLAYBOOK_CATEGORIES = 'Manage playbook categories',
  JIRA_WORKFLOW = 'Jira Workflow',
  SNOW_WORKFLOW = 'ServiceNow Workflow',
  ACCOUNT_PROMPT_OVERRIDE_SOURCE = 'Manage account prompt override source',
  CONFIGURE_FILTERS = 'Manage page filters',
  CONFIGURE_COLUMNS = 'Manage page columns',
}

export enum DateLimitOptions {
  SLIDING_WINDOW = 'Sliding Window',
  ACCOUNT_CREATION = 'Account Creation',
  EARLIEST_OPEN_AUDIT = 'Start of Earliest Open Audit',
  CURRENT_BOUNDARY = 'Current Boundary',
}

export const CUSTOM_DATE_LIMIT_OPTION = 'Custom' as DateLimitOptions

export const CUSTOM_DATE_LIMIT_VALUE = {
  title: CUSTOM_DATE_LIMIT_OPTION,
  dateLabel: 'Select a date',
  dateValue: null,
  isRecommended: false,
}

export const INITIAL_DATE_LIMIT_CONFIG: DateLimitConfigType = {
  [DateLimitOptions.SLIDING_WINDOW]: null,
  [DateLimitOptions.ACCOUNT_CREATION]: null,
  [DateLimitOptions.EARLIEST_OPEN_AUDIT]: null,
  [DateLimitOptions.CURRENT_BOUNDARY]: null,
}

export const MIN_SLIDING_MONTHS = 1
export const MAX_SLIDING_MONTHS = 36
export const RECOMMENDED_SLIDING_MONTHS = 12
// Below this, a window is short enough that it is worth asking whether the account meant
// it. Deliberately not the point where infrequent evidence starts aging out — that is
// nearer 12 months — because warning on every value under a year would fire on the ones an
// account may well have chosen on purpose. The copy is worded to match: a caution, not a
// precise claim about what happens at this exact boundary.
export const SHORT_SLIDING_WINDOW_MONTHS = 6

export const SHORT_SLIDING_WINDOW_WARNING =
  'A window this short can give unexpected results for controls on an annual or infrequent cadence, whose evidence may sit outside it for much of the year.'

export const DATE_LIMIT_ERROR_MESSAGE =
  'Sorry, there was an error setting the earliest evidence date. Please try again.'

export const AUDIT_SCAN_FREQUENCY_ERROR_MESSAGE =
  'Sorry, there was an error updating the control test frequency. Please try again.'

export const VALIDATE_IMAGE_TIMESTAMP_ERROR_MESSAGE =
  'Sorry, there was an error setting the image validation. Please try again.'

// Matched against stored notification and activity-log rows, so it must keep saying
// "date limit" — the setting's former name — even though the UI no longer does.
export const DATE_LIMIT_UPDATE_MESSAGE = 'updated date limit'

export const DATE_LIMIT_BANNER_MESSAGE =
  'The earliest evidence date was updated recently. Control tests run against the previous window now show as out of date.'

export const EARLIEST_EVIDENCE_DESCRIPTION =
  'Changing this date will affect which evidence Trustero AI considers'

export const ACCOUNT_SETTINGS_GRID_TEMPLATE = 'auto 1fr repeat(2, auto)'

export enum AccountSettingsTabKey {
  GLOBAL = 'global',
  COMPLIANCE = 'compliance',
  FILTERS = 'filters',
  COLUMNS = 'columns',
  NOTIFICATIONS = 'notifications',
}
